/****************************************************
 * Module Name:  userReport.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import Constants from "../../utility/constants";
import Select from "react-select";
import {
  Box,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import LocationProxy from "../../utility/proxy/manager/locationProxy";
import UserProxy from "../../utility/proxy/manager/userProxy";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import renderCellExpand from "../common/dataGridCellExpand";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import Utility from "../../utility/utility";
import { LoadingButton } from "@mui/lab"; import NoPermission from "../common/noPermission";
import CommonSnackbar from "../common/commonSnackBar";
import PageTitle from "../common/pageTitle";

const deleteDisableOption = [
  { value: "*", label: "-- ANY --" },
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

const fullOptionToValue = (selectedData) => {
  if (selectedData.value === "*") return [-1];
  let newArr = [];
  selectedData.map((option) => {
    newArr.push(option.value);
    return true;
  });
  return newArr.includes("*") ? [-1] : newArr;
};

const UserReport = () => {
  const [userType, setUserType] = useState([]);
  const [userTypeOption, setUserTypeOption] = useState([]);
  const [location, setLocation] = useState([]);
  const [locationOption, setLocationOption] = useState([]);
  const [deleted, setDeleted] = useState(deleteDisableOption[0]);
  const [disabled, setDisabled] = useState(deleteDisableOption[0]);
  const [listData, setListData] = useState([]);
  const [userReportListLoader, setUserReportListLoader] = useState(false);
  const [fetchData, setFetchData] = useState(false);
  const [access, setAccess] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const defaultOptions = { value: "*", label: "-- ANY --" };

  const [filterValues, setFilterValues] = useState({});

  const getAllLocationData = () => {
    LocationProxy.GetAllLocationDetailMini((response) => {
      setDataLocation(response);
      setFetchData(true);
    });
  };

  useEffect(() => {
    PageTitle("REPORT_USER");
    checkPermission();
  }, []);

  const checkPermission = () => {
    let allowed = Constants.USER_PERMISSION.find(
      (iPermission) =>
        iPermission.permissionid ===
        Constants.PERMISSIONS.REPORT_USER
    ) !== undefined
    if (!allowed) {
      setAccess(true)
      return <NoPermission />
    } else {
      getAllLocationData();
      setAllUserType();
    }
  }

  useEffect(() => {
    if (fetchData) {
      if (userType && location && disabled && deleted) {
        handleSearch(userType, location, disabled, deleted);
      }
    }
  }, [fetchData, userType, location, deleted, disabled]);

  const handleSearch = (userType, location, disabled, deleted) => {
    setUserReportListLoader(true);
    let nUserType = fullOptionToValue(userType);
    let nLocation = fullOptionToValue(location);
    let nDisabled = disabled.value === "*" ? "" : disabled.value;
    let nDeleted = deleted.value === "*" ? "" : deleted.value;
    UserProxy.UserReport(
      nUserType,
      nLocation,
      nDisabled,
      nDeleted,
      (response) => {
        if (response) {
          const uniqueFilterValues = {
            designation: Utility.getDistinctOptions(response, "designation"),
            createdby: Utility.getDistinctOptions(response, "createdby"),
            city: Utility.getDistinctOptions(response, "location"),
            country: Utility.getDistinctOptions(response, "country"),
          };
          setFilterValues(uniqueFilterValues);
          setListData(response);
          setFetchData(false);
          setUserReportListLoader(false);
        } else {
          setUserReportListLoader(false);
          setErrorSnackbar(true);
        }
      }
    );
  };

  const setDataLocation = (response) => {
    let optionsArray = [];
    optionsArray.push(defaultOptions);
    response.map((data) => {
      let option = { value: data.id, label: data.name + " | " + data.country };
      optionsArray.push(option);
      return true;
    });
    setLocationOption(optionsArray);
    setLocation(
      optionsArray.length > 0 ? optionsArray[0] : location
    );
  };

  const setAllUserType = () => {
    let optionsArray = [];
    optionsArray.push(defaultOptions);
    Object.keys(Constants.USER_TYPE).map((key) => {
      let option = { value: key, label: Constants.USER_TYPE[key] };
      optionsArray.push(option);
      return true;
    });
    setUserTypeOption(optionsArray);
    setUserType(
      optionsArray.length > 0 ? optionsArray[0] : userType
    );
  };

  const initialColumns = () => [
    {
      field: "id",
      headerName: "ID",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      minWidth: 50,
    },
    {
      field: "username",
      headerName: "USERNAME",
      headerAlign: "center",
      align: "center",
      minWidth: 170,
      renderCell: renderCellExpand,
    },
    {
      field: "first_name",
      headerName: "FIRST NAME",
      headerAlign: "center",
      align: "center",
      minWidth: 170,
      renderCell: renderCellExpand,
    },
    {
      field: "middle_name",
      headerName: "MIDDLE NAME",
      headerAlign: "center",
      align: "center",
      minWidth: 170,
      renderCell: renderCellExpand,
    },
    {
      field: "last_name",
      headerName: "LAST NAME",
      headerAlign: "center",
      align: "center",
      minWidth: 170,
      renderCell: renderCellExpand,
    },
    {
      field: "designation",
      headerName: "DESIGNATION",
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      type: "singleSelect",
      valueOptions: filterValues ? filterValues.designation : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "employeeid",
      headerName: "EMPLOYEE ID",
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    {
      field: "usertype",
      headerName: "USER TYPE",
      headerAlign: "center",
      align: "center",
      minWidth: 170,
      renderCell: renderCellExpand,
    },
    {
      field: "createdby",
      headerName: "CREATED BY",
      headerAlign: "center",
      align: "center",
      minWidth: 170,
      type: "singleSelect",
      valueOptions: filterValues ? filterValues.createdby : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "location",
      headerName: "CITY",
      headerAlign: "center",
      align: "center",
      minWidth: 170,
      type: "singleSelect",
      valueOptions: filterValues ? filterValues.city : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "country",
      headerName: "COUNTRY",
      headerAlign: "center",
      align: "center",
      minWidth: 170,
      type: "singleSelect",
      valueOptions: filterValues ? filterValues.country : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "created",
      headerName: "CREATED AT",
      headerAlign: "center",
      align: "center",
      minWidth: 180,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    },
    {
      field: "deleted",
      headerName: "DELETED",
      headerAlign: "center",
      align: "center",
      minWidth: 170,
      renderCell: (params) => {
        return params.value === false ? (
          <Box>
            <PersonIcon color="success" />
          </Box>
        ) : (
          <Box>
            <PersonOffIcon color="error" />
          </Box>
        );
      },
    },
    {
      field: "disabled",
      headerName: "DISABLED",
      headerAlign: "center",
      align: "center",
      minWidth: 170,
      renderCell: (params) => {
        return params.value === false ? (
          <Box>
            <PersonIcon color="success" />
          </Box>
        ) : (
          <Box>
            <PersonOutlineOutlinedIcon color="error" />
          </Box>
        );
      },
    },
  ];

  const columns = initialColumns();

  return (
    <div className="background">
      <CommonSnackbar
        show={errorSnackbar}
        message={Constants.APiFailErrorMessage}
        variant={"error"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Failed"}
        handleClose={() => setErrorSnackbar(false)}
      />
      {
        access ? <NoPermission /> : (
          <Box px={1}>
            <Box
              sx={{
                display: "flex",
                width: "100vw",
                mb: 3,
                mt: 2,
                flexWrap: "wrap",
                alignItems: "center",
                gap: "10px",
                pl: 1
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  minWidth: "370px",
                  maxWidth: "auto",
                  gap: "20px",
                }}
              >
                <Box sx={{ alignSelf: "center", mt: 1, width: "75px" }}>
                  <Typography>Location</Typography>
                </Box>
                <Box
                  sx={{
                    alignSelf: "center",
                    minWidth: "280px",
                    maxWidth: "450px",
                    mt: 1,
                  }}
                >
                  <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    className="basic-single"
                    classNamePrefix="select"
                    isSearchable={true}
                    name="location"
                    closeMenuOnSelect={false}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    isMulti
                    options={locationOption}
                    value={location}
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    onChange={(selectedOption) => setLocation(selectedOption)}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  minWidth: "400px",
                  maxWidth: "auto",
                  gap: "20px",
                }}
              >
                <Box sx={{ alignSelf: "center", mt: 1, width: "75px" }}>
                  <Typography>User Type</Typography>
                </Box>
                <Box
                  sx={{
                    alignSelf: "center",
                    minWidth: "300px",
                    maxWidth: "450px",
                    mt: 1,
                  }}
                >
                  <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    closeMenuOnSelect={false}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    isMulti
                    options={userTypeOption}
                    value={userType}
                    onChange={(e) => setUserType(e)}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: "20px" }}>
                <Typography sx={{ alignSelf: "center", width: "50px", mt: 1 }}>
                  Deleted
                </Typography>
                <Box
                  sx={{
                    minWidth: "150px",
                    maxWidth: "150px",
                    mt: 1,
                  }}
                >
                  <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    className="basic-single"
                    classNamePrefix="select"
                    value={deleted}
                    isSearchable={true}
                    name="Deleted"
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    options={deleteDisableOption}
                    onChange={(selectedOption) => setDeleted(selectedOption)}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: "20px" }}>
                <Typography sx={{ alignSelf: "center", width: "50px", mt: 1 }}>
                  Disabled
                </Typography>
                <Box
                  sx={{
                    minWidth: "150px",
                    maxWidth: "150px",
                    mt: 1,
                  }}
                >
                  <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    className="basic-single"
                    classNamePrefix="select"
                    value={disabled}
                    isSearchable={true}
                    name="Disabled"
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    options={deleteDisableOption}
                    onChange={(selectedOption) => setDisabled(selectedOption)}
                  />
                </Box>
              </Box>
              <LoadingButton
                loading={userReportListLoader}
                loadingPosition="start"
                variant="contained"
                onClick={() => handleSearch(userType, location, disabled, deleted)}
                startIcon={<SearchOutlinedIcon size="large" />}
                sx={{ mt: 1 }}
                disabled={
                  location.length === 0 || userType.length === 0
                }
              >
                Search
              </LoadingButton>
            </Box>
            <Box
              sx={{
                backgroundColor: '#fff',
                color: '#000',
                width: "100%",
                position: "relative",
                "& .MuiTablePagination-selectLabel": {
                  marginTop: "15px",
                },
                "& .MuiTablePagination-displayedRows": {
                  marginTop: "15px",
                },
                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                  fontWeight: Constants.defaultFontWeight,
                },
                "& .css-cc8tf1": {
                  fontWeight: Constants.defaultFontWeight,
                },
                height: "80vh",
              }}
            >
              <DataGridPro
                disableRowSelectionOnClick={true}
                getRowHeight={() => "auto"}
                sx={{
                  '& .MuiDataGrid-columnHeader': {
                    color: '#fff',
                    backgroundColor: 'grey',
                  },
                  "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                    py: "4px",
                  },
                  "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                    py: "12px",
                  },
                  "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                    py: "18px",
                  },

                  fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                }}
                density="compact"
                slots={{ toolbar: GridToolbar }}
                rows={listData}
                columns={columns}
                loading={userReportListLoader}
                getRowId={(row) => row.id}
                initialState={{
                  columns: {
                    columnVisibilityModel: {
                      // Hide columns id, the other columns will remain visible
                      id: false,
                    },
                  },
                  pagination: {
                    paginationModel: Constants.pageSize,
                  },
                }}
                pageSizeOptions={Constants.pageSizeOptions}
                pagination
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
              />
              <Box
                style={{
                  position: "absolute",
                  top: 5,
                  right: "270px",
                }}
              >
                {userReportListLoader ? (
                  <CircularProgress size="1rem" />
                ) : (
                  <Tooltip title="Reload List">
                    <RefreshOutlinedIcon
                      onClick={() =>
                        handleSearch(userType, location, disabled, deleted)
                      }
                      color="primary"
                      fontSize="medium"
                      sx={{ cursor: "pointer" }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        )}
    </div>
  );
};

export default UserReport;
